import React from 'react';

import { BACK, MASTER_READING_PAGES, MATCH_TICKETS } from './constants';
import { GetApp } from '@material-ui/icons';
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DescriptionIcon from '@mui/icons-material/Description';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import FilterListIcon from '@mui/icons-material/FilterList';
import { DEFAULT_PDF_ZOOM } from '../../global/constants';
import Utils from '../../Utils/Utils';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import RenderPurchaseOrderPDFPreview from '../../transaction/RenderPurchaseOrderPDFPreview';
import {
  getMatchPointPDFPreview,
  resetMatchPointPdfPreviewData,
} from '../../../redux/actions/settings';

import { matchPointPDFpreviewReadySelector } from '../../../redux/selectors/ticketOut';
import { matchSessionSelector } from '../../../redux/selectors/settings';
import withShipment from '../../../withShipment';
import CustomToolbar from '../../global/CustomToolbar';
import PrintIcon from '@mui/icons-material/Print';
import EmployeeTicketsTable from './EmployeeTicketsTable';
import { userInfoSelector } from '../../../redux/selectors/auth';
import './matchTickets.css';
import scrollAppToTop from '../../Utils/scrollToTop';
import {
  NAVY,
  INDIGO,
  INDIGO_LIGHT,
  LAVENDER,
  GREEN,
  RED,
  SLATE,
  PAGE_BG,
  GREEN_TINT,
  RED_TINT,
  STATUS,
  money,
  signed,
  netStatusKey,
  computeTotals,
} from './matchTicketsHelpers';

class MatchTicketsTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      filter: 'all',
      openRow: null,
      isMobile: window.matchMedia('(max-width: 800px)').matches,
      // report / pdf viewer
      scale: DEFAULT_PDF_ZOOM,
      showPdfViewer: false,
      pdfLoadSuccess: true,
      pdfData: [],
      pdfKind: 'all', // 'all' | machine number
    };
  }

  componentDidMount() {
    this.props.listMatchTickets(
      this.props.selectedSessionId,
      this.props.userInfo.selectedLocationId
    );
  }

  componentDidUpdate(prevProps) {
    if (
      !this.arrayEquals(prevProps.PDFpreviewReady, this.props.PDFpreviewReady)
    ) {
      this.setState({ pdfData: this.props.PDFpreviewReady });
    }
  }

  arrayEquals(arr1, arr2) {
    if (!arr1 || !arr2) return false;
    if (arr1.length !== arr2.length) return false;
    for (let i = 0; i < arr1.length; i++) {
      if (arr1[i] !== arr2[i]) return false;
    }
    return true;
  }

  // ── tickets lookup ─────────────────────────────────────────────────────────
  getTicketsForMachine = (machine) => {
    const group = (this.props.employeeTickets || []).find(
      (g) => String(g.machine) === String(machine)
    );
    return group ? group.tickets || [] : [];
  };

  openSheet = (row) => {
    this.setState(
      { openRow: { ...row, tickets: this.getTicketsForMachine(row.machine) } },
      () => scrollAppToTop()
    );
  };

  closeSheet = () => this.setState({ openRow: null });

  // ── pdf viewer ───────────────────────────────────────────────────────────
  showPdfErrorMessage = (isFailed) => {
    this.props.resetMatchPointPdfPreviewData();
    this.setState({ pdfLoadSuccess: !isFailed });
  };

  closePdfViewer = () => this.setState({ showPdfViewer: false, pdfData: '' });

  setZoom(zoom) {
    let newScale = Math.min(Math.max(this.state.scale + zoom, 1), 2);
    this.setState({ scale: newScale });
  }

  fetchSessionPdf = () => {
    this.props.getMatchPointPDFPreview(
      {
        sessionId: this.props.selectedSessionId,
        locationId: this.props.userInfo.selectedLocationId,
      },
      this.showPdfErrorMessage
    );
    this.setState({ showPdfViewer: true, pdfKind: 'all' });
  };

  fetchMachinePdf = (machine) => {
    this.props.getMatchPointPDFPreview(
      {
        sessionId: this.props.selectedSessionId,
        locationId: this.props.userInfo.selectedLocationId,
        machine,
      },
      this.showPdfErrorMessage
    );
    // Keep openRow set so backing out of the PDF returns to this machine's page.
    this.setState({ showPdfViewer: true, pdfKind: machine });
  };

  ymd = (display) => {
    const d = new Date(display);
    if (isNaN(d)) return Utils.formatTodayDate(new Date());
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  downloadFileName = () => {
    const session = this.props.matchSession || {};
    const loc = (session.locationName || 'Location').replace(/\s+/g, '-');
    const date = this.ymd(session.readingDateDisplay);
    const n = session.sessionNumber ?? '';
    if (this.state.pdfKind === 'all') {
      return `Match-Tickets_${loc}_${date}_Session-${n}.pdf`;
    }
    return `Machine-${this.state.pdfKind}_Employee-Tickets_${date}_Session-${n}.pdf`;
  };

  download = () => {
    Utils.handlePdfAction(
      this.state.pdfData,
      'download',
      this.downloadFileName()
    );
  };

  // ── render pieces ──────────────────────────────────────────────────────────
  renderSubHeader = (session) => (
    <div
      style={{
        padding: '14px 16px',
        background: `linear-gradient(90deg, ${INDIGO} 0%, ${INDIGO_LIGHT} 100%)`,
        borderRadius: 16,
      }}
    >
      <h1
        style={{
          margin: 0,
          color: '#fff',
          fontWeight: 800,
          fontSize: 20,
          lineHeight: 1.1,
        }}
      >
        Match Tickets
      </h1>
      <p
        style={{
          margin: '4px 0 0',
          color: 'rgba(255,255,255,.85)',
          fontSize: 12,
          fontWeight: 600,
        }}
      >
        Reading · {session.readingDateDisplay || '—'}
      </p>
      <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
        <span style={chipPill}>Session {session.sessionNumber ?? '—'}</span>
        {session.sessionDay && (
          <span style={chipPill}>{session.sessionDay}</span>
        )}
      </div>
    </div>
  );

  renderKpiBand = (totals) => {
    const netKey = netStatusKey(totals.net);
    const netLabel = STATUS[netKey].label;
    const netAmountColor =
      netKey === 'over_ticket_paid'
        ? RED_TINT
        : netKey === 'forgot_tickets'
        ? GREEN_TINT
        : '#fff';
    const netSentence =
      netKey === 'over_ticket_paid'
        ? 'More ticket money written than machines read.'
        : netKey === 'forgot_tickets'
        ? 'Money still available — tickets not written down.'
        : 'Readings and tickets match.';
    const net = Number(totals.net) || 0;

    return (
      <div
        style={{
          borderRadius: 16,
          padding: 16,
          background: NAVY,
          marginTop: 16,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <div>
            <p style={kpiEyebrow}>{netLabel}</p>
            <span
              style={{
                display: 'block',
                marginTop: 4,
                fontSize: 30,
                fontWeight: 800,
                letterSpacing: '-0.02em',
                color: netAmountColor,
              }}
            >
              {net < 0 ? '−' : net > 0 ? '+' : ''}
              {money(net)}
            </span>
            <p
              style={{
                margin: '4px 0 0',
                fontSize: 11,
                color: '#8b93c9',
                maxWidth: 200,
              }}
            >
              {netSentence}
            </p>
          </div>
          <div
            style={{
              flexShrink: 0,
              textAlign: 'right',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <div
              style={{
                borderRadius: 12,
                padding: '8px 12px',
                background: 'rgba(110,231,183,.12)',
              }}
            >
              <p style={{ ...kpiTileLabel, color: GREEN_TINT }}>
                Forgot Tickets
              </p>
              <p
                style={{
                  margin: 0,
                  fontSize: 16,
                  fontWeight: 800,
                  color: GREEN_TINT,
                }}
              >
                {money(totals.forgotAmount)}
              </p>
            </div>
            <div
              style={{
                borderRadius: 12,
                padding: '8px 12px',
                background: 'rgba(253,164,175,.12)',
              }}
            >
              <p style={{ ...kpiTileLabel, color: RED_TINT }}>
                Over Ticket Paid
              </p>
              <p
                style={{
                  margin: 0,
                  fontSize: 16,
                  fontWeight: 800,
                  color: RED_TINT,
                }}
              >
                {money(totals.overAmount)}
              </p>
            </div>
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 12,
            marginTop: 16,
          }}
        >
          <div
            style={{
              borderRadius: 12,
              padding: 12,
              background: 'rgba(255,255,255,.07)',
            }}
          >
            <p style={kpiWhiteLabel}>Reading Out</p>
            <p
              style={{
                margin: '2px 0 0',
                color: '#fff',
                fontSize: 18,
                fontWeight: 800,
              }}
            >
              {money(totals.readingTotal)}
            </p>
            <p style={{ margin: 0, fontSize: 11, color: '#8b93c9' }}>
              {totals.machineCount} machines
            </p>
          </div>
          <div
            style={{
              borderRadius: 12,
              padding: 12,
              background: 'rgba(255,255,255,.07)',
            }}
          >
            <p style={kpiWhiteLabel}>Ticket Out</p>
            <p
              style={{
                margin: '2px 0 0',
                color: '#fff',
                fontSize: 18,
                fontWeight: 800,
              }}
            >
              {money(totals.ticketTotal)}
            </p>
            <p style={{ margin: 0, fontSize: 11, color: '#8b93c9' }}>
              {totals.ticketCount} tickets
            </p>
          </div>
        </div>
      </div>
    );
  };

  renderChips = (totals) => {
    const chips = [
      {
        key: 'all',
        label: 'All',
        count: totals.machineCount,
        color: INDIGO,
        bg: LAVENDER,
      },
      {
        key: 'even',
        label: 'Even',
        count: totals.evenCount,
        color: SLATE,
        bg: STATUS.even.bg,
      },
      {
        key: 'forgot_tickets',
        label: 'Forgot Tickets',
        count: totals.forgotCount,
        color: GREEN,
        bg: STATUS.forgot_tickets.bg,
      },
      {
        key: 'over_ticket_paid',
        label: 'Over Ticket Paid',
        count: totals.overCount,
        color: RED,
        bg: STATUS.over_ticket_paid.bg,
      },
    ];
    return (
      <div
        className="mt-chip-row"
        style={{
          display: 'flex',
          gap: 8,
          overflowX: 'auto',
          paddingBottom: 4,
          marginTop: 16,
        }}
      >
        {chips.map((c) => {
          const active = this.state.filter === c.key;
          return (
            <button
              key={c.key}
              onClick={() => this.setState({ filter: c.key })}
              style={{
                flexShrink: 0,
                padding: '8px 14px',
                borderRadius: 12,
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
                background: active ? c.color : c.bg,
                color: active ? '#fff' : c.color,
                border: `1.5px solid ${active ? c.color : 'transparent'}`,
              }}
            >
              {c.label} · {c.count}
            </button>
          );
        })}
      </div>
    );
  };

  renderMachineCard = (r) => {
    const st = STATUS[r.status] || STATUS.even;
    const employees = (r.employees || []).join(' · ');
    return (
      <button
        key={r.machine}
        onClick={() => this.openSheet(r)}
        style={{
          width: '100%',
          textAlign: 'left',
          background: '#fff',
          borderRadius: 16,
          padding: 14,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          cursor: 'pointer',
          border: '1.5px solid #e9eaf4',
          boxShadow: '0 1px 2px rgba(10,14,42,.04)',
        }}
      >
        <div
          style={{
            flexShrink: 0,
            width: 44,
            height: 44,
            borderRadius: 12,
            background: LAVENDER,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <span
            style={{
              fontSize: 8,
              fontWeight: 700,
              letterSpacing: 1,
              color: '#8f95c9',
            }}
          >
            MCH
          </span>
          <span
            style={{
              fontSize: 14,
              fontWeight: 800,
              lineHeight: 1,
              color: INDIGO,
            }}
          >
            {r.machine}
          </span>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div>
              <p style={cardMiniLabel}>Reading Out</p>
              <p
                style={{
                  margin: 0,
                  fontSize: 14,
                  fontWeight: 700,
                  color: NAVY,
                }}
              >
                {money(r.readingOut)}
              </p>
            </div>
            <div style={{ color: '#c9cde0' }}>→</div>
            <div>
              <p style={cardMiniLabel}>Ticket Out</p>
              <p
                style={{
                  margin: 0,
                  fontSize: 14,
                  fontWeight: 700,
                  color: NAVY,
                }}
              >
                {money(r.ticketOut)}
              </p>
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              marginTop: 6,
              minWidth: 0,
            }}
          >
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                fontSize: 10,
                fontWeight: 700,
                padding: '2px 8px',
                borderRadius: 999,
                background: '#f1f2f8',
                color: '#6b7194',
                flexShrink: 0,
              }}
            >
              <ConfirmationNumberIcon style={{ fontSize: 10 }} />{' '}
              {r.ticketCount} {r.ticketCount === 1 ? 'ticket' : 'tickets'}
            </span>
            {employees && (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: '#9aa1c0',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {employees}
              </span>
            )}
          </div>
        </div>

        <div style={{ flexShrink: 0, textAlign: 'right' }}>
          <span
            style={{
              display: 'inline-block',
              fontSize: 11,
              fontWeight: 800,
              padding: '4px 10px',
              borderRadius: 8,
              background: st.bg,
              color: st.color,
            }}
          >
            {Number(r.diff) === 0 ? 'Even' : signed(r.diff)}
          </span>
          {r.status !== 'even' && (
            <p
              style={{
                margin: '4px 0 0',
                fontSize: 9,
                fontWeight: 700,
                color: st.color,
              }}
            >
              {st.label}
            </p>
          )}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 2,
              marginTop: 2,
            }}
          >
            <span style={{ fontSize: 10, fontWeight: 600, color: '#9aa1c0' }}>
              Tickets
            </span>
            <ChevronRightIcon style={{ fontSize: 13, color: '#9aa1c0' }} />
          </div>
        </div>
      </button>
    );
  };

  render() {
    const { showPdfViewer, pdfData } = this.state;
    const differenceList = this.props.differenceList || [];
    const session = this.props.matchSession || {};
    const totals =
      session.totals && Object.keys(session.totals).length
        ? session.totals
        : computeTotals(differenceList);

    const rows = differenceList.filter(
      (r) => this.state.filter === 'all' || r.status === this.state.filter
    );
    const sessionEmpty = differenceList.length === 0;

    let headerButtons = [];
    if (showPdfViewer) {
      headerButtons.push(
        {
          icon: <KeyboardBackspaceIcon />,
          handler: () => this.closePdfViewer(),
          title: BACK,
        },
        {
          icon: <GetApp />,
          handler: () => this.download(),
          title: 'Download PDF',
        },
        {
          icon: <PrintIcon />,
          title: 'Print PDF',
          handler: () => {
            if (!this.state.pdfData) return;
            Utils.handlePdfAction(
              this.state.pdfData,
              'print',
              this.downloadFileName()
            );
          },
        },
        {
          icon: <ZoomOutIcon />,
          handler: () => this.setZoom(-0.25),
          title: 'Zoom Out',
          disabled: false,
        },
        {
          divider: (
            <span style={{ padding: '5px', marginTop: '5px' }}>
              {this.state.scale * 100}%
            </span>
          ),
        },
        {
          icon: <ZoomInIcon />,
          handler: () => this.setZoom(0.25),
          title: 'Zoom In',
          disabled: false,
        }
      );
    } else if (this.state.openRow) {
      // Employee Tickets detail page — back returns to the machine list.
      headerButtons.push({
        icon: <KeyboardBackspaceIcon />,
        handler: () => this.closeSheet(),
        title: BACK,
      });
    } else {
      // When deep-linked from the Weekly Report, Back returns there; otherwise
      // it goes back to the Master Reading table.
      headerButtons.push({
        icon: <KeyboardBackspaceIcon />,
        handler: () =>
          this.props.onExit
            ? this.props.onExit()
            : this.props.setCurrentPage(MASTER_READING_PAGES.TABLE, {}),
        title: BACK,
      });
    }

    return (
      <React.Fragment>
        <CustomToolbar
          title={MATCH_TICKETS}
          createTitle={MATCH_TICKETS}
          backTitle={'Back To Menu'}
          onClick={() => {}}
          headerButtons={headerButtons}
        />

        {showPdfViewer ? (
          <RenderPurchaseOrderPDFPreview
            data={pdfData}
            creating={true}
            handlePdfLoadSuccess={this.props.handlePdfLoadSuccess}
            handlePdfLoadError={this.props.handlePdfLoadError}
            scale={this.state.scale}
          />
        ) : this.state.openRow ? (
          <EmployeeTicketsTable
            row={this.state.openRow}
            session={session}
            onOpenPdf={this.fetchMachinePdf}
          />
        ) : (
          <div style={{ background: PAGE_BG, padding: 16, minHeight: '100%' }}>
            {this.renderSubHeader(session)}
            {this.renderKpiBand(totals)}

            {!sessionEmpty && this.renderChips(totals)}

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                marginTop: 16,
              }}
            >
              {sessionEmpty ? (
                <div style={emptyBox}>
                  <FilterListIcon style={{ fontSize: 22, color: '#c9cde0' }} />
                  <p
                    style={{
                      margin: '8px 0 0',
                      fontSize: 14,
                      fontWeight: 700,
                      color: NAVY,
                    }}
                  >
                    No readings in this session
                  </p>
                </div>
              ) : rows.length === 0 ? (
                <div style={emptyBox}>
                  <FilterListIcon style={{ fontSize: 22, color: '#c9cde0' }} />
                  <p
                    style={{
                      margin: '8px 0 0',
                      fontSize: 14,
                      fontWeight: 700,
                      color: NAVY,
                    }}
                  >
                    No machines in this view
                  </p>
                  <p
                    style={{
                      margin: '4px 0 0',
                      fontSize: 12,
                      color: '#9aa1c0',
                    }}
                  >
                    Pick another status to see machines.
                  </p>
                </div>
              ) : (
                rows.map((r) => this.renderMachineCard(r))
              )}
            </div>

            <button
              onClick={this.fetchSessionPdf}
              disabled={sessionEmpty}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                padding: 14,
                borderRadius: 12,
                border: 'none',
                cursor: sessionEmpty ? 'not-allowed' : 'pointer',
                fontSize: 14,
                fontWeight: 700,
                color: '#fff',
                background: sessionEmpty ? '#b7bad6' : INDIGO,
                marginTop: 20,
              }}
            >
              <DescriptionIcon style={{ fontSize: 16 }} /> Open PDF — All
              machines
            </button>
          </div>
        )}
      </React.Fragment>
    );
  }
}

// ── inline style fragments ────────────────────────────────────────────────
const chipPill = {
  fontSize: 10,
  fontWeight: 800,
  padding: '2px 8px',
  borderRadius: 999,
  background: 'rgba(255,255,255,.2)',
  color: '#fff',
};
const kpiEyebrow = {
  margin: 0,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: 1.4,
  textTransform: 'uppercase',
  color: '#8b93c9',
};
const kpiTileLabel = {
  margin: 0,
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: 0.6,
  textTransform: 'uppercase',
};
const kpiWhiteLabel = {
  margin: 0,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: 0.6,
  textTransform: 'uppercase',
  color: '#8b93c9',
};
const cardMiniLabel = {
  margin: 0,
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: 0.6,
  textTransform: 'uppercase',
  color: '#9aa1c0',
};
const emptyBox = {
  background: '#fff',
  borderRadius: 16,
  padding: 32,
  textAlign: 'center',
  border: '1.5px dashed #dfe2f0',
};

const mapStateToProps = (state) => ({
  PDFpreviewReady: matchPointPDFpreviewReadySelector(state),
  userInfo: userInfoSelector(state),
  matchSession: matchSessionSelector(state),
});

const actionCreators = {
  getMatchPointPDFPreview,
  resetMatchPointPdfPreviewData,
};

export default withShipment(
  {
    actionCreators,
    mapStateToProps,
  },
  MatchTicketsTable
);
